<template>
  <Form
    ref="form"
    class="address-form-new"
    :fields="fields"
    :handleSubmit="handleSubmit"
    :isDisabled="isDisabled"
    :isSubmitBlocked="isSubmitBlocked"
    :submitButtonText="submitButtonText"
    :isSubmitButtonVisible="isSubmitButtonVisible"
    :isSubmitButtonForceEnabled="false"
    :isDisabledSubmitButtonInteractive="true"
    :isScrollingToInvalidFieldOnSubmit="isScrollingToInvalidFieldOnSubmit"
    @update="onValueChange($event.id, $event.value)"
    @update:isValid="$emit('update:isValid', $event)"
    @fieldValidation="trackFieldValidation($event)"
    @click-submit="$emit('click-submit')"
  >
    <slot slot="below-form-fields" name="below-address-form-new-fields" />
  </Form>
</template>

<script>
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import { filter, isEmpty as isEmptyObject, hasKeys } from '@/helpers/object'
import { isGoogleAutocompleteDisabled, DEFAULT_NEW_ADDRESS_VALUE } from '@/helpers/address'
import { isEmpty as isEmptyArray, sortArrayByPropName } from '@/helpers/array'
import { isOnlyLetters, hasNumber, requireHouseNumber, hasMaxChars } from '@/helpers/validation'
import Form, { VALUE_TYPES } from '@/components/_shared/Form/Forms/Form.vue'
import {
  setValue,
  getFieldIds,
  populateSelectField,
  getFieldById,
} from '@/components/_shared/Form/form-helper'
import { VALIDATION_TYPES } from '@/components/_shared/Form/form-field-interface-mixin'
import FormInput from '@/components/_shared/Form/Input/FormInput.vue'
import PlaceAutocompleteInput, {
  RESTRICTED_COUNTRIES_MAX,
  getAutocompletedFields,
} from '@/components/_shared/Form/Input/PlaceAutocompleteInput.vue'
import PhoneFormInput from '@/components/_shared/Form/Input/PhoneFormInput.vue'
import FormSelect, {
  isValidSelectOptions,
  PLACEHOLDER_VALUE,
} from '@/components/_shared/Form/Select/FormSelect.vue'

const MAX_ALLOWED_CHARS_DEFAULT = 35
const MAX_ALLOWED_CHARS_CITY = 30
const MAX_ALLOWED_CHARS_ZIP_CODE = 10
const MAX_ALLOWED_CHARS_PHONE = 15

export default {
  name: 'AddressFormNew',
  components: {
    Form,
    /* eslint-disable vue/no-unused-components */
    FormInput,
    PlaceAutocompleteInput,
    PhoneFormInput,
    FormSelect,
    /* eslint-enable vue/no-unused-components */
  },

  props: {
    address: {
      type: Object,
      default: () => ({}),
      validator(obj) {
        return isEmptyObject(obj) || !obj.countryData || hasKeys(obj.countryData, ['iso'])
      },
    },
    countries: {
      type: Array,
      required: true,
      validator: isValidSelectOptions,
    },
    states: {
      type: Array,
      required: true,
      validator: isValidSelectOptions,
    },
    language: {
      type: String,
      default: '',
    },
    handleSubmit: {
      type: Function,
    },
    submitButtonText: {
      type: String,
    },
    isDisabled: {
      type: Boolean,
    },
    isSubmitButtonVisible: {
      type: Boolean,
    },
    isScrollingToInvalidFieldOnSubmit: {
      type: Boolean,
    },
    fieldValidations: {
      type: Object,
      default: () => ({}),
    },
    fieldKeysOptional: {
      type: Array,
      default: () => [],
    },
    fieldKeysDisabled: {
      type: Array,
      default: () => [],
    },
    fieldKeysOrder: {
      type: Array,
      default: () => [],
    },
    fieldKeysHidden: {
      type: Array,
      default: () => [],
    },
    fieldKeysHiddenInitially: {
      type: Array,
      default: () => [],
    },
    fieldKeysHalfWidth: {
      type: Array,
      default: () => [],
    },
    restrictedCountriesForAutocomplete: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isSubmitBlocked: false,
      areFieldKeysHiddenInitiallyVisible: false,
      fields: [
        {
          component: FormInput,
          props: {
            id: 'firstname',
            label: this.$t('forms.first_name'),
            value: '',
            validations: [
              { validator: isOnlyLetters, message: this.$t('errors.no_special_char') },
              this.getMaxCharsValidation(MAX_ALLOWED_CHARS_DEFAULT),
            ],
            autocorrect: 'off',
            autocapitalize: 'off',
          },
        },
        {
          component: FormInput,
          props: {
            id: 'lastname',
            label: this.$t('forms.last_name'),
            value: '',
            validations: [
              { validator: isOnlyLetters, message: this.$t('errors.no_special_char') },
              this.getMaxCharsValidation(MAX_ALLOWED_CHARS_DEFAULT),
            ],
            autocorrect: 'off',
            autocapitalize: 'off',
          },
        },
        {
          component: FormInput,
          props: {
            id: 'address1',
            label: this.$t('forms.street_address'),
            value: '',
            restrictedCountries: [], // will be updated in restrictedCountriesForAutocomplete watcher
            validations: [
              this.getMaxCharsValidation(MAX_ALLOWED_CHARS_DEFAULT),
              {
                validator: this.hasHouseNumber,
                message: this.$t('house_number_warning'),
                type: VALIDATION_TYPES.warning,
              },
            ],
            autocorrect: 'off',
            autocapitalize: 'off',
          },
          events: {
            focus: this.onAutocompleteFocus,
            blur: this.onAutocompleteBlur,
            placechanged: this.onPlaceChange,
          },
        },
        {
          component: FormInput,
          props: {
            id: 'address2',
            label: this.$t('forms.street_address_2'),
            value: '',
            validations: [this.getMaxCharsValidation(MAX_ALLOWED_CHARS_DEFAULT)],
          },
        },
        {
          component: FormInput,
          props: {
            id: 'city',
            label: this.$t('forms.city'),
            value: '',
            validations: [this.getMaxCharsValidation(MAX_ALLOWED_CHARS_CITY)],
          },
        },
        {
          component: FormSelect,
          props: {
            id: 'state_id',
            label: this.$t('forms.state'),
            value: PLACEHOLDER_VALUE,
            options: [],
          },
          optionsPlaceholder: [{ text: this.$t('forms.state'), value: PLACEHOLDER_VALUE }],
          valueType: VALUE_TYPES.number,
          defaultFormValue: PLACEHOLDER_VALUE,
          defaultServerValue: DEFAULT_NEW_ADDRESS_VALUE,
        },
        {
          component: FormSelect,
          props: {
            id: 'country_id',
            label: this.$t('forms.country'),
            value: PLACEHOLDER_VALUE,
            options: [],
          },
          optionsPlaceholder: [],
          valueType: VALUE_TYPES.number,
          defaultFormValue: PLACEHOLDER_VALUE,
        },
        {
          component: FormInput,
          props: {
            id: 'zipcode',
            label: this.$t('forms.zip'),
            value: '',
            validations: [this.getMaxCharsValidation(MAX_ALLOWED_CHARS_ZIP_CODE)],
          },
        },
        {
          component: PhoneFormInput,
          props: {
            id: 'phone',
            label: this.$t('forms.phone') + ' ' + this.$t('forms.phone_placeholder_info'),
            value: '',
            countryIso: '', // will be updated in selectedCountryIso watcher
            validations: [this.getMaxCharsValidation(MAX_ALLOWED_CHARS_PHONE)],
          },
        },
        {
          component: FormInput,
          props: {
            id: 'neighborhood',
            label: this.$t('forms.neighborhood'),
            value: '',
          },
        },
        {
          component: FormInput,
          props: {
            id: 'document_number',
            label: this.$t('forms.document_number'),
            value: '',
          },
        },
      ],
    }
  },

  computed: {
    selectedCountryIso() {
      return this.address?.countryData?.iso
    },

    autocompleteComponent() {
      return this.isAutocompleteEnabled ? PlaceAutocompleteInput : FormInput
    },

    isAutocompleteEnabled() {
      return !isGoogleAutocompleteDisabled(this.selectedCountryIso)
    },
  },

  watch: {
    address: {
      immediate: true,
      deep: true,
      handler() {
        if (!isEmptyObject(this.address)) {
          this.fields.forEach(({ props }) => {
            setValue(this, props.id, this.address[props.id], this.fields)
          })
          if (this.hasAutocompleteFieldSet(this.address)) {
            this.areFieldKeysHiddenInitiallyVisible = true
          }
        }
      },
    },

    isAutocompleteEnabled: {
      immediate: true,
      handler() {
        if (!this.isAutocompleteEnabled) this.areFieldKeysHiddenInitiallyVisible = true
      },
    },

    selectedCountryIso: {
      immediate: true,
      handler() {
        if (this.selectedCountryIso) {
          const addressInput = getFieldById('address1', this.fields)
          if (addressInput) this.$set(addressInput, 'component', this.autocompleteComponent)
          const phoneInput = getFieldById('phone', this.fields)
          if (phoneInput) this.$set(phoneInput.props, 'countryIso', this.selectedCountryIso)
        }
      },
    },

    countries: {
      immediate: true,
      handler() {
        this.populateSelectField('country_id', this.countries, this.fields)
      },
    },

    states: {
      immediate: true,
      handler() {
        this.populateSelectField('state_id', this.states, this.fields)
      },
    },

    fieldValidations: {
      immediate: true,
      handler: 'updateFieldsValidations',
    },

    language: {
      immediate: true,
      handler: 'updateLanguage',
    },

    fieldKeysOptional: {
      immediate: true,
      handler: 'updateFieldsRequirement',
    },

    fieldKeysDisabled: {
      immediate: true,
      handler: 'updateFieldsDisabled',
    },

    fieldKeysOrder: {
      immediate: true,
      handler: 'updateFieldsOrder',
    },

    fieldKeysHidden: {
      immediate: true,
      handler: 'updateFieldsVisibility',
    },

    areFieldKeysHiddenInitiallyVisible: 'updateFieldsVisibility',

    fieldKeysHalfWidth: {
      immediate: true,
      handler: 'addFieldsHalfWidthClass',
    },

    restrictedCountriesForAutocomplete: {
      immediate: true,
      handler: 'updateRestrictedCountriesForAutocomplete',
    },
  },

  methods: {
    /* public methods */
    getFormData() {
      return this.$refs.form?.getFormData() || {}
    },

    async validate() {
      await this.$refs.form.validate()
    },

    async setError(error) {
      await this.$refs.form.setError(error)
    },

    focusFirstInvalidField() {
      const id = this.getFirstInvalidFieldId()
      id ? this.focusField(id) : this.focusSubmit()
    },

    focusField(id) {
      this.$refs.form?.getFormFieldById(id)?.focus()
    },

    focusSubmit() {
      this.$refs.form?.getFormSubmitButton()?.focus()
    },

    /* private methods */
    onAutocompleteFocus() {
      this.isSubmitBlocked = true
      this.areFieldKeysHiddenInitiallyVisible = true
    },

    onAutocompleteBlur() {
      this.isSubmitBlocked = false
    },

    async onPlaceChange(place) {
      // We need to change the country field first in order to have hidden fields updated.
      const countryId = this.getCountryId(place)
      const countryInput = getFieldById('country_id', this.fields)
      if (countryId !== countryInput.props.value) {
        this.onValueChange('country_id', countryId)
        await this.waitForCountryUpdate()
      }
      // When the user chooses an address with a country different to the current one via autocomplete
      // and this address has a state (place.state), we cannot select the state in the dropdown (!stateId),
      // because states for this country need to be fetched first by parent view.
      // Therefore, we register a watcher on states to do the selection once states are fetched.
      if (place.state) {
        const stateId = this.getStateId(place)
        if (stateId) {
          this.onValueChange('state_id', stateId)
        } else {
          this.waitForStatesUpdate().then(() => {
            this.onValueChange('state_id', this.getStateId(place))
          })
        }
      }
      // Update other address fields (that are not hidden).
      const nonHiddenFields = this.fields.filter(field => !field.isHidden)
      const fieldValues = filter(place, getFieldIds(nonHiddenFields))
      for (const id in fieldValues) {
        this.onValueChange(id, fieldValues[id])
      }

      await this.$nextTick()
      this.focusFirstInvalidField()
    },

    onValueChange(id, value) {
      this.$emit('update', { id, value })
    },

    populateSelectField(id, values, fields) {
      populateSelectField(this, id, values, fields)
    },

    updateFieldsValidations() {
      this.fields.forEach(field => {
        const validations = this.fieldValidations[field.props.id]
        if (validations) {
          if (field.props.validations) validations.unshift(...field.props.validations)
          this.$set(field.props, 'validations', validations)
        }
      })
    },

    updateLanguage() {
      const address1Input = getFieldById('address1', this.fields)
      this.$set(address1Input.props, 'language', this.language)
    },

    updateFieldsRequirement() {
      this.fields.forEach(field => {
        const isRequired = !this.fieldKeysOptional.includes(field.props.id)
        this.$set(field.props, 'isRequired', isRequired)
      })
    },

    updateFieldsDisabled() {
      this.fields.forEach(field => {
        const isDisabled = this.fieldKeysDisabled.includes(field.props.id)
        this.$set(field.props, 'isDisabled', isDisabled)
      })
    },

    updateFieldsOrder() {
      if (isEmptyArray(this.fieldKeysOrder)) return
      this.fields.forEach(field => {
        const index = this.fieldKeysOrder.indexOf(field.props.id)
        const order = index >= 0 ? index : Infinity
        this.$set(field, 'order', order)
      })
      sortArrayByPropName(this.fields, 'order')
    },

    updateFieldsVisibility() {
      this.fields.forEach(field => {
        const isHidden =
          this.fieldKeysHidden.includes(field.props.id) ||
          (!this.areFieldKeysHiddenInitiallyVisible &&
            this.fieldKeysHiddenInitially.includes(field.props.id))
        this.$set(field, 'isHidden', isHidden)
      })
    },

    addFieldsHalfWidthClass() {
      this.fields.forEach(field => {
        this.$set(field.props, 'class', '')
        if (this.fieldKeysHalfWidth.includes(field.props.id)) {
          this.$set(field.props, 'class', 'form__field--half')
        }
      })
    },

    updateRestrictedCountriesForAutocomplete() {
      // if restricted countries maximum is exceeded, don't pass any restriction
      const restrictedCountries =
        this.restrictedCountriesForAutocomplete.length <= RESTRICTED_COUNTRIES_MAX
          ? this.restrictedCountriesForAutocomplete
          : []
      const address1Input = getFieldById('address1', this.fields)
      this.$set(address1Input.props, 'restrictedCountries', restrictedCountries)
    },

    trackFieldValidation({ id, isValid }) {
      if (!isValid) {
        notifyExternalServices({
          action: ACTIONS.errors,
          details: {
            action: 'address_input_field',
            label: `address || ${id}`,
          },
        })
      }
    },

    hasHouseNumber(value) {
      return hasNumber(value) || !requireHouseNumber(this.selectedCountryIso)
    },

    getMaxCharsValidation(max) {
      return {
        validator: value => hasMaxChars(value, max),
        message: this.$t('errors.characters_limit', { MAX: max }),
      }
    },

    getFirstInvalidFieldId() {
      const invalidField = this.fields.find(field => !this.$refs.form.isFieldValid(field))
      return invalidField?.props.id
    },

    getCountryId({ country, countryIso }) {
      return this.countries.find(({ text, iso }) => text === country || iso === countryIso)?.value
    },

    getStateId({ state }) {
      return this.states.find(({ text }) => text === state)?.value
    },

    hasAutocompleteFieldSet(address) {
      return getAutocompletedFields().some(
        fieldKey => address[fieldKey] !== undefined && address[fieldKey] !== ''
      )
    },

    async waitForCountryUpdate() {
      return this.watchOnce('selectedCountryIso')
    },

    async waitForStatesUpdate() {
      return this.watchOnce('states')
    },

    watchOnce(prop) {
      return new Promise(resolve => {
        const unwatch = this.$watch(prop, () => {
          resolve()
          unwatch()
        })
      })
    },
  },
}
</script>

<i18n src="@/locales/address_form.json">
</i18n>
